
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/auth';
import CustomLinksField from './CustomLinksField';

import { sounds } from '@/utils/soundEffects';
import { Form } from '@/components/ui/form';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { uploadImage, UploadProgress } from '@/utils/imageHandling';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { ImageIcon, User2Icon, LinkIcon, AlertTriangle } from 'lucide-react';
import { getManualCounts, upsertManualCount, isSupabaseConnected } from '@/services/social-manual-service';
import { ProfileEmbeddingService } from '@/services/profile-embedding-service';
import { profileUpdateSchema, safeParse } from '@/lib/validation/schemas';

interface EditProfileFormProps {
  profile: Profile;
  onCancel: () => void;
}

interface FormData {
  display_name: string;
  bio: string;
  favorite_quote: string;
  custom_links?: any[];
}

type TabId = 'images' | 'info' | 'links' | 'reach' | 'tags';

interface TabItem {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const EditProfileForm = ({ profile, onCancel }: EditProfileFormProps) => {
  const form = useForm<FormData>({
    defaultValues: {
      display_name: profile.display_name || '',
      bio: profile.bio || '',
      favorite_quote: profile.favorite_quote || '',
      custom_links: profile.custom_links || []
    }
  });

  const [activeTab, setActiveTab] = useState<TabId>('images');
  const [isLoading, setIsLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(profile.profile_image_url || null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(profile.banner_image_url || null);
  const [avatarProgress, setAvatarProgress] = useState<UploadProgress>({ progress: 0, state: 'idle' });
  const [bannerProgress, setBannerProgress] = useState<UploadProgress>({ progress: 0, state: 'idle' });
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const tabs: TabItem[] = [
    { id: 'images', label: 'Profile Images', icon: <ImageIcon className="h-5 w-5" /> },
    { id: 'info', label: 'Basic Info', icon: <User2Icon className="h-5 w-5" /> },
    { id: 'links', label: 'Social Links', icon: <LinkIcon className="h-5 w-5" /> },
    { id: 'reach', label: 'Reach', icon: <User2Icon className="h-5 w-5" /> },
    { id: 'tags', label: 'Community Tags', icon: <User2Icon className="h-5 w-5" /> },
  ];

  // Manual follower counts state (loaded from DB)
  const [manualCounts, setManualCounts] = useState<Record<string, number>>({});

  React.useEffect(() => {
    let mounted = true;
    getManualCounts(profile.id)
      .then((data) => {
        if (mounted) setManualCounts(data);
      })
      .catch((e) => console.warn('Failed to load manual counts', e));
    return () => {
      mounted = false;
    };
  }, [profile.id]);

  const handleImageSelect = (file: File, type: 'avatar' | 'banner') => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file",
        variant: "destructive"
      });
      return;
    }
    
    // Check file size before proceeding (25MB max)
    const MAX_SIZE = 25 * 1024 * 1024; // 25MB in bytes
    if (file.size > MAX_SIZE) {
      toast({
        title: "File too large",
        description: "Maximum file size is 25MB. Please choose a smaller image.",
        variant: "destructive"
      });
      return;
    }

    if (type === 'avatar') {
      setAvatarFile(file);
      setAvatarProgress({ progress: 0, state: 'idle' });
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setBannerFile(file);
      setBannerProgress({ progress: 0, state: 'idle' });
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: FormData) => {
    console.log("Submitting form data:", data);
    setIsLoading(true);
    try {
      // Handle avatar upload if file is selected
      let profileImageUrl = profile.profile_image_url;
      if (avatarFile) {
        try {
          const uploadedAvatarUrl = await uploadImage(
            avatarFile,
            profile.id,
            'avatar',
            setAvatarProgress
          );
          console.log('Avatar uploaded successfully:', uploadedAvatarUrl);
          profileImageUrl = uploadedAvatarUrl;
        } catch (error) {
          console.error('Error in avatar upload:', error);
          sounds.error();
          toast({
            title: "Upload Failed",
            description: "Failed to upload profile picture. Please try again.",
            variant: "destructive"
          });
          setIsLoading(false);
          return;
        }
      }

      // Handle banner upload if file is selected
      let bannerImageUrl = profile.banner_image_url;
      if (bannerFile) {
        try {
          const uploadedBannerUrl = await uploadImage(
            bannerFile,
            profile.id,
            'banner',
            setBannerProgress
          );
          console.log('Banner uploaded successfully:', uploadedBannerUrl);
          bannerImageUrl = uploadedBannerUrl;
        } catch (error) {
          console.error('Error in banner upload:', error);
          sounds.error();
          toast({
            title: "Upload Failed",
            description: "Failed to upload banner image. Please try again.",
            variant: "destructive"
          });
          setIsLoading(false);
          return;
        }
      }

      // Validate the banner URL format if it exists
      if (bannerImageUrl && !bannerImageUrl.startsWith('http')) {
        console.warn('Banner image URL is not properly formatted:', bannerImageUrl);
        // The URL might be a relative path, we'll keep it as is
      }

      // Get custom links data for embedding generation only
      // We won't update these in the database to avoid the unnest(jsonb) error
      const customLinks = form.watch('custom_links') || [];
      const filteredLinks = customLinks.filter(
        (link: { url: string; label: string }) => link.url && link.label
      );
      console.log('Custom links data (not updating):', JSON.stringify(filteredLinks));

      // Create updated profile object for embedding generation
      const originalProfile = profile;
      const updatedProfile = {
        ...profile,
        display_name: data.display_name,
        bio: data.bio,
        favorite_quote: data.favorite_quote || '',
        profile_image_url: profileImageUrl,
        banner_image_url: bannerImageUrl
      };

      // SECURITY: Validate profile data before update
      const validationResult = safeParse(profileUpdateSchema, {
        display_name: data.display_name,
        bio: data.bio,
        favorite_quote: data.favorite_quote,
        profile_image_url: profileImageUrl,
        banner_image_url: bannerImageUrl,
        custom_links: data.custom_links,
      });

      if (!validationResult.success) {
        const error = validationResult as { success: false; error: string };
        toast({
          title: "Validation error",
          description: error.error,
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }

      const validatedData = validationResult.data;

      // Check if we need to generate new embeddings
      const hasEmbeddingChanges = ProfileEmbeddingService.hasEmbeddingRelevantChanges(
        originalProfile,
        updatedProfile
      );

      // Update profile ONLY if relevant fields actually changed to avoid unnecessary triggers
      if (hasEmbeddingChanges) {
        console.log('Detected embedding-relevant changes, updating basic profile fields');
        try {
          // Create payload with only basic fields (already validated)
          const basicFields = {
            display_name: data.display_name || '',
            bio: data.bio || '',
            favorite_quote: data.favorite_quote || '',
            profile_image_url: profileImageUrl || '',
            banner_image_url: bannerImageUrl || '',
            updated_at: new Date().toISOString()
          };

          console.log('Update payload:', basicFields);
          const { error: updateError } = await supabase
            .from('profiles')
            .update(basicFields)
            .eq('id', profile.id);

          if (updateError) {
            console.error('Error updating profile:', updateError);
            throw updateError;
          }

          console.log('Profile updated successfully');
        } catch (error) {
          console.error('Error in profile update transaction:', error);
          throw error;
        }
      } else {
        console.log('No embedding-relevant changes to profile fields; skipping profiles update');
      }

      // Generate embeddings for profile if there were changes to relevant fields
      if (hasEmbeddingChanges) {
        console.log('Starting async profile embedding generation');
        // Trigger embedding generation but don't wait for it to complete
        try {
          ProfileEmbeddingService.generateEmbedding({
            profileId: profile.id,
            silentMode: false
          }).catch((embeddingError) => {
            console.error('Error generating profile embedding:', embeddingError);
          });
        } catch (embeddingError) {
          console.error('Failed to start embedding generation:', embeddingError);
          // Non-blocking error, continue with profile update
        }
      }

      // Save manual follower counts (best-effort, non-blocking for profile basics)
      try {
        // Minimal connectivity preflight
        const connected = await isSupabaseConnected();
        if (!connected) {
          toast({ title: 'Not connected', description: 'Supabase connection is unavailable. Please try again.', variant: 'destructive' });
          throw new Error('Supabase not connected');
        }
        const entries = Object.entries(manualCounts).filter(([_, v]) => !Number.isNaN(Number(v)));
        if (entries.length) {
          await Promise.all(
            entries.map(([platform, count]) => upsertManualCount(profile.id, platform as any, Number(count)))
          );
          // Ensure profile landing page reflects new totals immediately
          await queryClient.invalidateQueries({ queryKey: ['profile-manual-counts', profile.id] });
        }
      } catch (e) {
        console.warn('Failed saving manual follower counts (non-blocking):', e);
        const anyErr = e as any;
        toast({
          title: 'Failed to save Reach',
          description: anyErr?.message || 'An error occurred while saving follower counts.',
          variant: 'destructive'
        });
      }

      // Invalidate and refetch profile data
      await queryClient.invalidateQueries({ queryKey: ['profile', profile.username] });
      await queryClient.invalidateQueries({ queryKey: ['profileById', profile.id] });

      sounds.success();
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully."
      });

      // Call onCancel to close the edit mode
      onCancel();

      // Only navigate if we have a username
      if (profile.username) {
        navigate(`/profile/${profile.username}`);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      sounds.error();
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Watch bio for live character counter
  const bioValue = form.watch('bio') || '';

  return (
    <ErrorBoundary>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Tab Navigation */}
          <div className="flex overflow-x-auto mb-4 border-b border-white/10 bg-[#111418] rounded-lg">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors",
                  "border-b-2 flex-shrink-0",
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-white/70 hover:text-white hover:border-white/30"
                )}
                onClick={() => setActiveTab(tab.id)}
              >
                {React.cloneElement(tab.icon as React.ReactElement, { className: "h-4 w-4" })}
                <span className="whitespace-nowrap">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="pb-2">
            {/* Images Tab */}
            {activeTab === 'images' && (
              <div className="flex flex-col items-center gap-4 animate-in fade-in duration-300">
                <div className="w-full space-y-4">
                  <ImageUpload
                    id="avatar"
                    type="avatar"
                    label="Profile Picture"
                    onChange={(file) => handleImageSelect(file, 'avatar')}
                    preview={avatarPreview}
                    isLoading={isLoading}
                    uploadProgress={avatarProgress}
                  />

                  <ImageUpload
                    id="banner"
                    type="banner"
                    label="Banner Image"
                    onChange={(file) => handleImageSelect(file, 'banner')}
                    preview={bannerPreview}
                    isLoading={isLoading}
                    uploadProgress={bannerProgress}
                    className="w-full"
                  />
                </div>
              </div>
            )}

            {/* Basic Info Tab */}
            {activeTab === 'info' && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div className="space-y-1.5">
                  <Label htmlFor="display_name" className="text-sm font-medium">Display Name</Label>
                  <Input
                    id="display_name"
                    placeholder="Your display name"
                    {...form.register('display_name', { required: true })}
                    className="bg-[#111418] border-white/20 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 h-10 text-sm px-3"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="bio" className="text-sm font-medium">Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell others about yourself..."
                    maxLength={250}
                    {...form.register('bio', { maxLength: { value: 250, message: 'Bio cannot exceed 250 characters.' } })}
                    className="bg-[#111418] border-white/20 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 min-h-[100px] text-sm p-3 resize-none"
                  />
                  <div className="flex justify-between text-xs text-white/60">
                    <span>{bioValue.length}/250</span>
                    {form.formState.errors.bio?.message && (
                      <span className="text-red-400">{String(form.formState.errors.bio.message)}</span>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="favorite_quote" className="text-sm font-medium">Favorite Quote</Label>
                  <Input
                    id="favorite_quote"
                    placeholder="Your favorite quote"
                    {...form.register('favorite_quote')}
                    className="bg-[#111418] border-white/20 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 h-10 text-sm px-3"
                  />
                </div>
              </div>
            )}

            {/* Social Links Tab */}
            {activeTab === 'links' && (
              <div className="animate-in fade-in duration-300">
                <CustomLinksField form={form} />
              </div>
            )}

            {/* Tags Tab - Community Tag Adoption */}
            {activeTab === 'tags' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                <div className="border border-white/10 rounded-lg p-4 space-y-4">
                  <h3 className="text-lg font-semibold">Community Tags</h3>
                  <p className="text-sm text-white/60">
                    Adopt a community tag to show your affiliation. This feature is coming soon and will allow you to represent a community with a custom tag that appears on your profile.
                  </p>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-sm text-white/60">
                      Community tag adoption will be available in a future update. You'll be able to browse communities, select their tags, and display them on your profile.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Reach Tab */}
            {activeTab === 'reach' && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-2.5 flex gap-2 items-start">
                  <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs">
                    Enter your follower counts truthfully. Inflating numbers may result in account restrictions or a ban from FOMO.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="reach_instagram" className="text-sm font-medium">Instagram</Label>
                    <Input
                      id="reach_instagram"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="e.g. 12500"
                      value={manualCounts['instagram'] ?? ''}
                      onChange={(e) => setManualCounts((m) => ({ ...m, instagram: Number(e.target.value.replace(/\D/g, '')) || 0 }))}
                      className="bg-[#111418] border-white/20 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 h-10 text-sm px-3"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="reach_tiktok" className="text-sm font-medium">TikTok</Label>
                    <Input
                      id="reach_tiktok"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="e.g. 9800"
                      value={manualCounts['tiktok'] ?? ''}
                      onChange={(e) => setManualCounts((m) => ({ ...m, tiktok: Number(e.target.value.replace(/\D/g, '')) || 0 }))}
                      className="bg-[#111418] border-white/20 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 h-10 text-sm px-3"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="reach_x" className="text-sm font-medium">X (Twitter)</Label>
                    <Input
                      id="reach_x"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="e.g. 4500"
                      value={manualCounts['x'] ?? ''}
                      onChange={(e) => setManualCounts((m) => ({ ...m, x: Number(e.target.value.replace(/\D/g, '')) || 0 }))}
                      className="bg-[#111418] border-white/20 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 h-10 text-sm px-3"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="reach_youtube" className="text-sm font-medium">YouTube</Label>
                    <Input
                      id="reach_youtube"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="e.g. 21000"
                      value={manualCounts['youtube'] ?? ''}
                      onChange={(e) => setManualCounts((m) => ({ ...m, youtube: Number(e.target.value.replace(/\D/g, '')) || 0 }))}
                      className="bg-[#111418] border-white/20 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 h-10 text-sm px-3"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Tags Tab - Community Tag Adoption */}
            {activeTab === 'tags' && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div className="border border-white/10 rounded-lg p-3 space-y-3">
                  <h3 className="text-base font-semibold">Community Tags</h3>
                  <p className="text-xs text-white/60">
                    Adopt a community tag to show your affiliation. This feature is coming soon and will allow you to represent a community with a custom tag that appears on your profile.
                  </p>
                  <div className="bg-white/5 rounded-lg p-2.5">
                    <div className="text-xs text-white/60">
                      Community tag adoption will be available in a future update. You'll be able to browse communities, select their tags, and display them on your profile.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Button Container */}
          <div className="mt-4 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
              className="px-3 py-2 h-9 text-sm"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="px-3 py-2 h-9 text-sm bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>
    </ErrorBoundary>
  );
};

export default EditProfileForm;
